import { trackClick } from "../lib/analytics";
import Seo from "../components/Seo";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CalendarCheck, Check, ChevronDown, Eye, Loader2, Lock, Minus, ShieldCheck, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import TierBadge from "../components/TierBadge";
import AdminReaderPreview, { OFFER_VIEWS, readerViewLabel } from "../components/AdminReaderPreview";
import { TIER_LABEL, TIER_ORDER, tierRank, tierStyle } from "../lib/tiers";
import useOfferCarousel from "../lib/useOfferCarousel";

const TAGLINE = {
  free: "Pour découvrir",
  essentiel: "Pour apprendre en profondeur",
  pro: "Pour passer à la pratique",
  max: "Pour échanger avec l'équipe",
};

// Tableau comparatif : offre minimum requise pour chaque ligne.
// La rubrique « Portefeuilles Side B » est construite à partir des portefeuilles publiés (voir plus bas).
const STATIC_ROWS = [
  ["Analyses", [["Analyses gratuites", "free"], ["Analyses premium", "essentiel"]]],
  ["Cours", [["Cours gratuits", "free"], ["Cours premium : rétrospectives, concepts, vocabulaire", "essentiel"]]],
  [
    "Outils",
    [
      ["Graphiques avancés", "free"],
      ["Projection DCA", "free"],
      ["Backtest simple", "essentiel"],
      ["Wallet DCA multi-actifs", "essentiel"],
      ["Monte Carlo", "pro"],
      ["Liquidation Map", "pro"],
    ],
  ],
];
const COMMUNITY_ROWS = [
  "Communauté",
  [["Newsletter", "free"], ["Communauté WhatsApp privée", "max"]],
];

const FAQ = [
  [
    "Puis-je résilier à tout moment ?",
    "Oui. L'abonnement est sans engagement : vous le résiliez en quelques clics depuis Mon compte, rubrique « Gérer mon abonnement ».",
  ],
  [
    "Que se passe-t-il après la résiliation ?",
    "Vous gardez l'accès à votre offre jusqu'à la fin du mois déjà payé. Aucun nouveau prélèvement n'est effectué, puis votre compte repasse en offre gratuite.",
  ],
  [
    "Suis-je remboursé si je résilie en cours de mois ?",
    "Non, il n'y a pas de remboursement au prorata. En contrepartie, vous profitez de votre offre jusqu'au dernier jour de la période payée.",
  ],
  [
    "Comment changer d'offre ?",
    "Choisissez la nouvelle offre sur cette page ou depuis Mon compte. Le changement se fait sur l'espace de paiement sécurisé Stripe, qui vous indique la date d'effet et le montant avant de confirmer.",
  ],
  [
    "Comment se passe le paiement ?",
    "Le paiement est géré par Stripe, prestataire de paiement sécurisé. Side B n'a jamais accès à vos coordonnées bancaires.",
  ],
  [
    "Les contenus sont-ils des conseils en investissement ?",
    "Non. Les analyses, cours, outils et portefeuilles sont publiés à but éducatif et ne tiennent pas compte de votre situation personnelle. Investir comporte un risque de perte en capital.",
  ],
];

const fmtPrice = (meta) => `${(meta?.price_eur ?? 0).toString().replace(".", ",")} €`;

export default function UpgradePage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = !!(user && user.role === "admin");
  const [loading, setLoading] = useState(null);
  const [faqOpen, setFaqOpen] = useState(0);
  // Fenêtre de confirmation avant paiement (nouvel abonnement) : renonciation au droit de rétractation
  const [confirmTier, setConfirmTier] = useState(null);
  // Téléphone : offre affichée dans le comparatif (une seule à la fois, choisie avec le sélecteur)
  const [compareTier, setCompareTier] = useState(null);

  // Aperçu admin : simule un visiteur, un membre gratuit ou un abonné (aucun paiement en aperçu)
  const [previewAs, setPreviewAs] = useState("admin");
  const isPreviewing = isAdmin && previewAs !== "admin";
  const isGuest = isPreviewing ? previewAs === "guest" : !user;
  const currentTier = isPreviewing
    ? ["essentiel", "pro", "max"].includes(previewAs)
      ? previewAs
      : "free"
    : user?.subscription_tier || "free";

  const { data: cfg } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  // Téléphone : les cartes d'offres défilent sur le côté, en commençant par l'offre actuelle (ou « Pro »)
  const carousel = useOfferCarousel(
    cfg ? TIER_ORDER.length : 0,
    Math.max(0, TIER_ORDER.indexOf(!isGuest && currentTier !== "free" ? currentTier : "pro"))
  );
  // Portefeuilles publiés : alimentent automatiquement le tableau comparatif
  const { data: portfolios = [] } = useQuery({
    queryKey: ["sideb-portfolios-offers"],
    queryFn: async () => (await api.get("/sideb/portfolios")).data,
  });

  const tableRows = useMemo(() => {
    const pf = portfolios
      .filter((p) => p.published !== false)
      .sort((a, b) => tierRank(a.tier_required) - tierRank(b.tier_required) || (a.name || "").localeCompare(b.name || "", "fr"))
      .map((p) => [p.name, p.tier_required || "free", true]);
    return [
      ...STATIC_ROWS,
      ["Portefeuilles Side B", [...pf, ["Alerte à chaque mouvement des portefeuilles suivis", "pro"]]],
      COMMUNITY_ROWS,
    ];
  }, [portfolios]);

  const startCheckout = async (tier) => {
    if (isPreviewing) {
      // Aperçu : on montre la fenêtre de confirmation d'un nouvel abonnement, sans lancer de paiement
      if (currentTier === "free") setConfirmTier(tier);
      else toast.info("Aperçu admin : aucun paiement n'est lancé");
      return;
    }
    if (!user) {
      nav("/login", { state: { from: "/upgrade" } });
      return;
    }
    // Déjà abonné : on ne crée pas un 2e abonnement, on ouvre l'espace de paiement Stripe.
    if ((user.subscription_tier || "free") !== "free") {
      setLoading(tier);
      try {
        const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
        window.location.href = data.url;
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossible d'ouvrir l'espace d'abonnement");
        setLoading(null);
      }
      return;
    }
    // Nouvel abonnement : fenêtre de confirmation (accès immédiat + renonciation à la rétractation)
    setConfirmTier(tier);
  };

  // Clic sur « Payer et accéder immédiatement » : l'accord est enregistré côté serveur avant le paiement Stripe
  const confirmAndPay = async () => {
    if (isPreviewing) {
      toast.info("Aperçu admin : aucun paiement n'est lancé");
      return;
    }
    const tier = confirmTier;
    trackClick("subscribe");
    const meta = cfg?.tiers?.[tier];
    setLoading(tier);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: meta?.lookup_key,
        origin_url: window.location.origin,
        waiver_accepted: true,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le paiement");
      setLoading(null);
    }
  };

  if (!cfg) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
      </div>
    );
  }

  const greenBtn =
    "w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium h-10 shadow bg-emerald-500 text-black hover:bg-emerald-300 disabled:opacity-60";
  const outlineBtn =
    "w-full inline-flex items-center justify-center gap-2 rounded-md text-sm h-10 border border-white/15 hover:bg-white/5 disabled:opacity-60";
  const neutralBox = "w-full inline-flex items-center justify-center gap-2 rounded-md text-sm h-10 bg-white/5 text-zinc-400";

  const renderCta = (t) => {
    const name = TIER_LABEL[t];
    const busy = loading === t;
    if (t === "free") {
      if (isGuest)
        return (
          <Link to="/login" className={outlineBtn} data-testid="tier-cta-free">
            Créer un compte gratuit
          </Link>
        );
      if (currentTier === "free")
        return (
          <div className={neutralBox} data-testid="tier-current-free">
            Votre offre actuelle
          </div>
        );
      return (
        <Link to="/account" className="w-full inline-flex items-center justify-center rounded-md text-sm h-10 text-zinc-400 hover:text-zinc-200">
          Gérer depuis Mon compte
        </Link>
      );
    }
    if (!isGuest && t === currentTier)
      return (
        <div className={`${neutralBox} text-zinc-300`} data-testid={`tier-current-${t}`}>
          <Check className="w-4 h-4" /> Votre offre actuelle
        </div>
      );
    if (isGuest || currentTier === "free")
      return (
        <button type="button" onClick={() => startCheckout(t)} disabled={busy} className={greenBtn} data-testid={`tier-cta-${t}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Choisir {name} <ArrowRight className="w-4 h-4" /></>}
        </button>
      );
    return tierRank(t) > tierRank(currentTier) ? (
      <button type="button" onClick={() => startCheckout(t)} disabled={busy} className={greenBtn} data-testid={`tier-cta-${t}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Passer à {name} <ArrowRight className="w-4 h-4" /></>}
      </button>
    ) : (
      <button type="button" onClick={() => startCheckout(t)} disabled={busy} className={outlineBtn} data-testid={`tier-cta-${t}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Changer pour ${name}`}
      </button>
    );
  };

  const highlightCol = isGuest ? null : currentTier;

  return (
    <div className="min-h-screen">
      <Seo title="Abonnements" description="Passez Essentiel ou Max : accédez à toutes les analyses, portefeuilles suivis et outils premium de Side B." path="/upgrade" />
      {isPreviewing && (
        <div className="border-b border-violet-500/20 bg-violet-500/[0.08]" data-testid="admin-preview-banner">
          <div className="mx-auto max-w-6xl px-6 py-2.5 flex flex-wrap items-center justify-between gap-3 text-xs">
            <span className="text-violet-200 flex items-center gap-2">
              <Eye className="w-3.5 h-3.5" /> Aperçu admin : vous voyez les offres comme
              <b className="font-semibold text-white">{readerViewLabel(previewAs, OFFER_VIEWS)}</b>
            </span>
            <button
              type="button"
              onClick={() => setPreviewAs("admin")}
              className="text-violet-300 hover:text-white underline-offset-4 hover:underline"
            >
              Revenir à ma vue
            </button>
          </div>
        </div>
      )}

      <section className="relative border-b border-white/5">
        <div className="absolute inset-0 grid-noise opacity-40" />
        <div className="relative mx-auto max-w-6xl px-6 py-8 sm:py-16">
          <div className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-2 sm:mb-3">Offres</div>
          <h1 className="font-display font-black text-[2rem] leading-tight sm:text-5xl">Choisissez votre offre</h1>
          <p className="mt-2 sm:mt-4 text-[15px] sm:text-base text-zinc-400 max-w-2xl">
            Chaque offre inclut la précédente. Sans engagement : vous changez d'offre ou résiliez quand vous voulez, depuis
            votre compte.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-12">
        <div
          ref={carousel.ref}
          className="flex md:grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 overflow-x-auto md:overflow-visible snap-x snap-mandatory scrollbar-none -mx-6 px-6 md:mx-0 md:px-0 pt-3 md:pt-0 pb-1"
          data-testid="tier-cards"
        >
          {TIER_ORDER.map((t) => {
            const meta = cfg.tiers?.[t] || {};
            const s = tierStyle(t);
            const featured = t === "pro";
            const isCurrent = !isGuest && t === currentTier;
            return (
              <div
                key={t}
                className={`relative shrink-0 w-[82%] md:w-auto snap-center rounded-xl border p-6 flex flex-col ${featured ? s.soft : "bg-card border-white/5"} ${
                  isCurrent ? "ring-1 ring-white/20" : ""
                }`}
                data-testid={`tier-card-${t}`}
              >
                <div className={`absolute inset-x-0 top-0 h-[3px] rounded-t-xl ${s.bar} ${t === "free" ? "opacity-40" : ""}`} />
                {featured && (
                  <span className="absolute -top-3 left-6 text-[10px] uppercase tracking-[0.2em] bg-emerald-500 text-black px-2 py-0.5 rounded">
                    Recommandé
                  </span>
                )}
                <div className="flex items-center justify-between gap-2">
                  <h3 className={`font-display font-black text-2xl ${s.title}`}>{meta.name || TIER_LABEL[t]}</h3>
                  {isCurrent && <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400">Votre offre</span>}
                </div>
                <p className="text-sm text-zinc-500 mt-1">{TAGLINE[t]}</p>
                <div className="mt-4 flex items-baseline gap-1">
                  <span className="font-mono-num text-4xl font-bold">{fmtPrice(meta)}</span>
                  <span className="text-zinc-500 text-sm">/mois</span>
                </div>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {(meta.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-zinc-200">
                      <Check className={`w-4 h-4 mt-0.5 shrink-0 ${s.text}`} /> {f}
                    </li>
                  ))}
                </ul>
                <div className="mt-6">{renderCta(t)}</div>
              </div>
            );
          })}
        </div>
        <div className="md:hidden mt-3 flex justify-center gap-2" data-testid="tier-cards-dots">
          {TIER_ORDER.map((t, i) => (
            <button
              key={t}
              type="button"
              onClick={() => carousel.goTo(i)}
              className={`h-1.5 rounded-full transition-all ${carousel.active === i ? "w-5 bg-white/70" : "w-1.5 bg-white/20"}`}
              aria-label={`Voir l'offre ${TIER_LABEL[t]}`}
            />
          ))}
        </div>
        {!isGuest && currentTier !== "free" && (
          <p className="mt-4 text-center text-xs text-zinc-500">
            Les changements d'offre se font sur l'espace de paiement sécurisé Stripe, qui vous indique le montant avant de confirmer.
          </p>
        )}
        <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-3 text-sm text-zinc-400">
          <span className="inline-flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-zinc-500" /> Paiement sécurisé par Stripe
          </span>
          <span className="inline-flex items-center gap-2">
            <Check className="w-4 h-4 text-zinc-500" /> Sans engagement
          </span>
          <span className="inline-flex items-center gap-2">
            <CalendarCheck className="w-4 h-4 text-zinc-500" /> Accès conservé jusqu'à la fin du mois payé
          </span>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-6 pt-16">
        <h2 className="font-display font-black text-2xl mb-5">Comparer les offres</h2>
        {/* Téléphone : sélecteur d'offre + liste de ce qui est inclus (le tableau ne tient pas en largeur) */}
        {(() => {
          const shown = compareTier || (currentTier === "pro" || currentTier === "max" ? "max" : "pro");
          return (
            <div className="sm:hidden" data-testid="tier-compare-mobile">
              <div className="sticky top-14 z-10 -mx-6 px-6 pt-1 pb-3 bg-[#0A0A0A]">
                <div className="grid grid-cols-4 gap-1 p-1 rounded-lg border border-white/10 bg-white/[0.02]" role="tablist" aria-label="Choisir une offre">
                  {TIER_ORDER.map((t) => (
                    <button
                      key={t}
                      type="button"
                      role="tab"
                      aria-selected={shown === t}
                      onClick={() => setCompareTier(t)}
                      className={`relative h-9 rounded-md border text-[13px] font-medium transition-colors ${
                        shown === t ? tierStyle(t).badge : "border-transparent text-zinc-500"
                      }`}
                      data-testid={`tier-compare-tab-${t}`}
                    >
                      {TIER_LABEL[t]}
                      {highlightCol === t && <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-emerald-400" aria-label="Votre offre" />}
                    </button>
                  ))}
                </div>
                <div className="mt-2 text-center text-xs text-zinc-500">
                  {TIER_LABEL[shown]} · {fmtPrice(cfg.tiers?.[shown])}/mois{highlightCol === shown ? " · votre offre" : ""}
                </div>
              </div>
              <div className="rounded-xl border border-white/5 bg-card px-4 pb-2">
                {tableRows.map(([group, rows]) => (
                  <div key={group}>
                    <div className="pt-5 pb-2 text-[10px] uppercase tracking-[0.2em] text-zinc-500">{group}</div>
                    {rows.map(([label, min, isPortfolio]) => {
                      const ok = tierRank(shown) >= tierRank(min);
                      return (
                        <div key={label} className="flex items-center justify-between gap-3 py-2.5 border-t border-white/5">
                          <span className={`text-sm ${ok ? "text-zinc-200" : "text-zinc-600"}`}>
                            {label}
                            {isPortfolio && <TierBadge tier={min} className="ml-2 align-middle" />}
                          </span>
                          {ok ? (
                            <Check className={`w-4 h-4 shrink-0 ${tierStyle(shown).text}`} aria-label="Inclus" />
                          ) : (
                            <Minus className="w-4 h-4 shrink-0 text-zinc-700" aria-label="Non inclus" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}
        <div className="hidden sm:block rounded-xl border border-white/5 bg-card overflow-x-auto" data-testid="tier-compare">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="border-b border-white/5">
                <th className="py-3 px-3 text-left text-sm font-medium text-zinc-400">Ce qui est inclus</th>
                {TIER_ORDER.map((t) => (
                  <th key={t} className={`py-3 px-3 text-center w-[13%] ${highlightCol === t ? "bg-white/[0.04]" : ""}`}>
                    <div className={`font-display font-black text-base ${tierStyle(t).title}`}>{TIER_LABEL[t]}</div>
                    <div className="text-[11px] text-zinc-500 font-normal">{fmtPrice(cfg.tiers?.[t])}/mois</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map(([group, rows]) => [
                <tr key={`g-${group}`}>
                  <td colSpan={5} className="pt-6 pb-2 px-3 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    {group}
                  </td>
                </tr>,
                ...rows.map(([label, min, isPortfolio]) => (
                  <tr key={`${group}-${label}`} className="border-t border-white/5">
                    <td className="py-2.5 px-3 text-sm text-zinc-300">
                      {label}
                      {isPortfolio && <TierBadge tier={min} className="ml-2 align-middle" />}
                    </td>
                    {TIER_ORDER.map((t) => (
                      <td key={t} className={`py-2.5 px-3 text-center ${highlightCol === t ? "bg-white/[0.04]" : ""}`}>
                        {tierRank(t) >= tierRank(min) ? (
                          <Check className={`w-4 h-4 inline ${tierStyle(t).text}`} aria-label="Inclus" />
                        ) : (
                          <Minus className="w-4 h-4 inline text-zinc-700" aria-label="Non inclus" />
                        )}
                      </td>
                    ))}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs text-zinc-600">La liste des portefeuilles se met à jour automatiquement selon l'offre de chacun.</p>
      </section>

      <section className="mx-auto max-w-3xl px-6 pt-12 pb-8 sm:py-16">
        <h2 className="font-display font-black text-2xl mb-5 text-center">Questions fréquentes</h2>
        <div className="rounded-xl border border-white/5 bg-card divide-y divide-white/5" data-testid="tier-faq">
          {FAQ.map(([q, a], i) => (
            <div key={q}>
              <button
                type="button"
                onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left text-sm font-medium text-zinc-100 hover:text-white"
                aria-expanded={faqOpen === i}
              >
                {q}
                <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${faqOpen === i ? "rotate-180" : ""}`} />
              </button>
              {faqOpen === i && <p className="px-5 pb-4 -mt-1 text-sm text-zinc-400 leading-relaxed">{a}</p>}
            </div>
          ))}
        </div>
      </section>

      {confirmTier && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-4" data-testid="checkout-confirm">
          <div
            className={`w-full max-w-lg rounded-xl border bg-[#121214] shadow-2xl ${tierStyle(confirmTier).border}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-confirm-title"
          >
            <div className="flex items-start justify-between px-6 pt-5">
              <div>
                <div className={`text-[10px] uppercase tracking-[0.2em] ${tierStyle(confirmTier).text}`}>Offre {TIER_LABEL[confirmTier]}</div>
                <div id="checkout-confirm-title" className="font-display font-black text-2xl mt-1">
                  {fmtPrice(cfg.tiers?.[confirmTier])} <span className="text-sm font-normal text-zinc-500">/ mois</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setConfirmTier(null)}
                disabled={loading === confirmTier}
                className="text-zinc-500 hover:text-white"
                aria-label="Fermer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <ul className="px-6 mt-4 space-y-1.5 text-sm text-zinc-400">
              {[
                "Renouvellement automatique chaque mois, sans engagement",
                "Résiliable à tout moment depuis Mon compte",
                "Accès conservé jusqu'à la fin du mois payé, pas de remboursement au prorata",
              ].map((t) => (
                <li key={t} className="flex gap-2">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${tierStyle(confirmTier).text}`} /> {t}
                </li>
              ))}
            </ul>
            <div className="px-6 mt-5 pt-5 border-t border-white/5 pb-5">
              <p className="text-sm text-zinc-300 leading-relaxed">
                En cliquant sur « Payer et accéder immédiatement », je demande l'accès immédiat à mon abonnement et je renonce à mon
                droit de rétractation de 14 jours.
              </p>
              <button
                type="button"
                onClick={confirmAndPay}
                disabled={loading === confirmTier}
                className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium h-11 bg-emerald-500 text-black hover:bg-emerald-300 disabled:opacity-60"
                data-testid="checkout-confirm-pay"
              >
                {loading === confirmTier ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                Payer et accéder immédiatement
              </button>
              <p className="mt-3 text-center text-xs text-zinc-500">
                En continuant, vous acceptez les{" "}
                <Link to="/cgv" className="text-zinc-300 underline underline-offset-4">
                  Conditions générales de vente
                </Link>
                .
              </p>
              <p className="mt-1 text-center text-xs text-zinc-500 inline-flex w-full justify-center items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> Paiement sécurisé par Stripe
              </p>
            </div>
          </div>
        </div>
      )}

      {isAdmin && <AdminReaderPreview value={previewAs} onChange={setPreviewAs} views={OFFER_VIEWS} />}
    </div>
  );
}
